import { Suspense, useMemo, useState } from 'react';
import { Clock, Flag, Inbox, ListOrdered, Shield, SkipForward, Square, Trash2 } from 'lucide-react';
import { formatLeft, useLeft } from '@gmp/async-kit/ui';
import { navigate } from '../app/router';
import type { GameEnvelope } from '@gmp/shared-types/browser';
import { Countdown, Leaderboard, PartyUiContext, PhaseSwap, Sheet, Stage, usePartyUi, type PartyUi } from '@gmp/shared-ui';
import { GAME_UI, PHASE_LABEL, screenKey } from '../app/games';
import { gameInfo } from '../app/catalog';
import { LoadingScreen } from '../app/Loading';
import { party } from '../net/client';

/** In-game frame shared by every game: header with round + synced countdown, scores and host tools. */
export function GameShell({ game }: { game: GameEnvelope }) {
  const ui = usePartyUi();
  const { room, reducedMotion } = ui;
  const [sheet, setSheet] = useState<null | 'scores' | 'host'>(null);
  const { Player, Display } = GAME_UI[game.gameId];
  // Party members without a seat in this game (spectators, late joiners, players who sat out a Grudge Match)
  // get the server's public display view, so they see the game's TV screen instead of the controls.
  const seated = game.scores.some((s) => s.playerId === room.you.id);
  const watchUi = useMemo<PartyUi>(() => ({ ...ui, display: true, act: async () => ({ ok: false, error: 'You are watching this game' }) }), [ui]);
  const g = gameInfo(game.gameId);
  const p = game.phase;
  const entryIds = new Set(((game.view as { entries?: { id: string }[] }).entries ?? []).map((e) => e.id));
  const reportTotal = Object.entries(room.reports).filter(([id]) => entryIds.has(id)).reduce((s, [, n]) => s + n, 0);
  return (
    <Stage game={game.gameId}>
      <header className="sticky top-0 z-20 safe-pad safe-top pb-2 bg-[color-mix(in_oklab,var(--stage)_85%,transparent)] backdrop-blur">
        <div className="flex items-center gap-3 max-w-3xl mx-auto">
          <div className="flex-1 min-w-0" data-shell="title">
            <p className="font-display font-black leading-tight truncate" style={{ color: 'var(--accent)' }}>{g.name}</p>
            <p className="text-xs text-mute font-bold uppercase tracking-wider" data-testid="phase-label">
              {p.round > 0 ? `Round ${p.round}/${p.totalRounds} · ` : ''}{PHASE_LABEL[p.name] ?? p.name}
            </p>
          </div>
          <button className="btn btn-sm btn-secondary" onClick={() => setSheet('scores')} aria-label="Scores"><ListOrdered size={18} /></button>
          {room.you.isHost && (
            <button className="btn btn-sm btn-secondary relative" onClick={() => setSheet('host')} aria-label={`Host controls${reportTotal ? `, ${reportTotal} reports` : ''}`} data-testid="host-controls">
              <Shield size={18} />
              {reportTotal > 0 && <span className="absolute -top-1 -right-1 size-5 rounded-full bg-bad text-white text-[11px] grid place-items-center font-bold">{reportTotal}</span>}
            </button>
          )}
          {g.category === 'async' ? (
            <>
              <button className="btn btn-sm btn-secondary" onClick={() => { party.stepOut(); navigate({ name: 'parties' }); }} aria-label="My parties (you keep your seat)" data-testid="step-out"><Inbox size={18} /></button>
              <AsyncLeft endsAt={p.endsAt} />
            </>
          ) : (
            <Countdown endsAt={p.endsAt} startedAt={p.startedAt} size={52} quiet={p.name !== 'ACTIVE_ROUND' && p.name !== 'VOTING'} />
          )}
        </div>
      </header>
      <PhaseSwap id={screenKey(p)} reduced={reducedMotion}>
        <Suspense fallback={<GameLoading gameId={game.gameId} />}>
          {seated ? (
            <Player game={game} view={game.view} />
          ) : (
            <PartyUiContext.Provider value={watchUi}>
              <p className="text-center text-mute text-sm font-bold safe-pad" data-testid="watching-game">You’re watching this one. You’ll be in the next game.</p>
              <Display game={game} view={game.view} />
            </PartyUiContext.Provider>
          )}
        </Suspense>
      </PhaseSwap>
      <Sheet open={sheet === 'scores'} onClose={() => setSheet(null)} title="Scores">
        <Leaderboard scores={game.scores} />
      </Sheet>
      <Sheet open={sheet === 'host'} onClose={() => setSheet(null)} title="Host controls">
        <HostTools game={game} onDone={() => setSheet(null)} />
      </Sheet>
    </Stage>
  );
}

export function GameLoading({ gameId, big }: { gameId?: string; big?: boolean }) {
  return <LoadingScreen label="Loading the game…" gameId={gameId} big={big} />;
}

function HostTools({ game, onDone }: { game: GameEnvelope; onDone: () => void }) {
  const { room } = usePartyUi();
  const [confirmEnd, setConfirmEnd] = useState(false);
  const entries = ((game.view as { entries?: { id: string; imageUrl?: string | null; mediaUrl?: string; caption?: string | null; kind?: string }[] }).entries ?? []);
  const act = async (cmd: Parameters<typeof party.host>[0]) => {
    const r = await party.host(cmd);
    if (!r.ok) party.notify(r.error ?? 'Not possible right now', 'bad');
  };
  return (
    <div className="grid gap-4">
      <button className="btn btn-secondary" onClick={() => { void act({ cmd: 'skip_phase' }); onDone(); }} data-testid="skip-phase"><SkipForward size={18} /> Skip to next step</button>
      <button className="btn btn-secondary" onClick={() => act({ cmd: 'lock', locked: !room.locked })}>{room.locked ? 'Reopen party to new players' : 'Lock party (no new players)'}</button>
      {entries.length > 0 && (
        <section className="grid gap-2">
          <h3 className="font-display font-black flex items-center gap-2"><Flag size={16} /> Remove an entry</h3>
          <p className="text-mute text-sm">Removed entries disappear for everyone and score nothing.</p>
          <ul className="grid grid-cols-3 gap-2">
            {entries.map((e, i) => (
              <li key={e.id} className="rounded-xl bg-ink-3 p-2 flex flex-col gap-1 items-center">
                {e.imageUrl ? <img src={e.imageUrl} alt="" className="size-16 object-cover rounded-lg" /> : e.kind === 'image' && e.mediaUrl ? <img src={e.mediaUrl} alt="" className="size-16 object-cover rounded-lg" /> : <span className="text-xs text-center line-clamp-3">{e.caption ?? `Clip #${i + 1}`}</span>}
                {room.reports[e.id] ? <span className="chip text-[11px] bg-bad/25 border-bad" data-testid="report-count"><Flag size={12} /> {room.reports[e.id]} report{room.reports[e.id] === 1 ? '' : 's'}</span> : null}
                <button className="btn btn-sm btn-secondary !min-h-8 text-bad" onClick={() => act({ cmd: 'remove_submission', submissionId: e.id })} aria-label={`Remove entry ${i + 1}`}><Trash2 size={14} /> #{i + 1}</button>
              </li>
            ))}
          </ul>
        </section>
      )}
      {confirmEnd ? (
        <div className="grid grid-cols-2 gap-2">
          <button className="btn btn-secondary" onClick={() => setConfirmEnd(false)}>Keep playing</button>
          <button className="btn" style={{ background: 'var(--color-bad)' }} onClick={() => { void act({ cmd: 'end_game' }); onDone(); }}>End now</button>
        </div>
      ) : (
        <button className="btn btn-ghost text-bad" onClick={() => setConfirmEnd(true)}><Square size={18} /> End game early</button>
      )}
    </div>
  );
}

/** Time left in an async step: "38s" to "2d 5h", ticking once a second. */
function AsyncLeft({ endsAt }: { endsAt: number | null }) {
  const left = useLeft(endsAt);
  if (left === null) return null;
  return (
    <span className="chip py-1.5 shrink-0" role="timer" aria-label={`Time left: ${formatLeft(left)}`} data-testid="async-left">
      <Clock size={14} aria-hidden /> <span className="font-bold tabular-nums">{formatLeft(left)}</span>
    </span>
  );
}
